import React, { useMemo, useState, useEffect } from "react";

interface RatingScaleContent {
  label: string;
  minimumValue: number;
  maximumValue: number;
  numberOfSteps: number;
  minimumLabel: string;
  maximumLabel: string;
  centerLabel?: string;
}

interface RatingScaleComponentProps {
  value: number | string;
  onChange: (value: number | string) => void;
  content: RatingScaleContent;
}

const RatingScaleComponent: React.FC<RatingScaleComponentProps> = ({
  value,
  onChange,
  content,
}) => {
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);

  const stepLabels = useMemo(() => {
    const steps = [];
    for (let i = content.minimumValue; i <= content.maximumValue; i += 1) {
      steps.push(i);
    }
    return steps;
  }, [content.minimumValue, content.maximumValue]);

  const handleValueChange = (newValue: number) => {
    setSelectedNumber(newValue);
    const formattedValue = `${newValue} out of ${content.maximumValue}`;
    onChange(formattedValue);
  };

  useEffect(() => {
    if (typeof value === "string") {
      const numericValue = parseInt(value);
      if (!isNaN(numericValue)) {
        setSelectedNumber(numericValue);
      } else {
        setSelectedNumber(null); // No valid value
      }
    } else if (typeof value === "number") {
      setSelectedNumber(value);
    } else {
      setSelectedNumber(null); // No value provided
    }
  }, [value]);

  const circleSize = content.numberOfSteps <= 7 ? "32px" : "24px";
  const fontSize = content.numberOfSteps <= 7 ? "14px" : "12px";

  return (
    <div>
      <div className="mt-4 px-4">
        <div
          className="mb-2 font-bold"
          style={{
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {content.label}
        </div>
        <div className="relative py-5">
          <div className="absolute -top-0 left-0 right-0 flex justify-between">
            {stepLabels.map((step) => (
              <div
                key={step}
                className={`rounded-full flex items-center justify-center cursor-pointer transition-transform duration-300 ease-in-out hover:scale-110 hover:shadow-xl ${
                  step === selectedNumber
                    ? "bg-electrum-250 text-white font-bold scale-110 shadow-lg"
                    : "bg-gray-200 text-black shadow-sm"
                }`}
                style={{
                  width: circleSize,
                  height: circleSize,
                  fontSize: fontSize,
                }}
                onClick={() => handleValueChange(step)}
              >
                {step}
              </div>
            ))}
          </div>
        </div>

        {(content.minimumLabel ||
          content.centerLabel ||
          content.maximumLabel) && (
          <div className="flex justify-between relative min-h-[2.5em] px-1 py-1">
            {content.minimumLabel && (
              <div className="w-1/4 text-left break-words">
                {content.minimumLabel}
              </div>
            )}
            {content.centerLabel && (
              <div className="absolute left-1/2 -translate-x-1/2 w-1/4 text-center break-words">
                {content.centerLabel}
              </div>
            )}
            {content.maximumLabel && (
              <div className="w-1/4 text-right break-words">
                {content.maximumLabel}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingScaleComponent;
